// Curated shortlist of 150 popular Google Fonts. Loaded on-demand via the
// Google Fonts CSS2 endpoint (see /app/frontend/src/lib/googleFontLoader.js).
// Keeps the admin dropdown searchable and instant without a Google API key
// or a runtime fetch.
//
// `category` maps to Google Fonts' own taxonomy so admins can filter mentally.
// `weights` is the subset of the 100-900 range Google actually publishes for
// that family (loading a weight the family doesn't have returns a 400).
// Fonts that don't publish specific weights get sensible defaults (400,700).
//
// Order roughly follows Google Fonts popularity rankings as of late 2025.

export const DEFAULT_HEADLINE_TYPOGRAPHY = Object.freeze({
  font_family: "Playfair Display",
  font_size: 40,
  font_weight: 500,
  line_height: 0.95,
  letter_spacing: -0.02,
});

export const GOOGLE_FONTS = [
  // ---- Serifs (editorial / display) ----
  { family: "Playfair Display", category: "serif", weights: [400, 500, 600, 700, 800, 900] },
  { family: "Cormorant Garamond", category: "serif", weights: [300, 400, 500, 600, 700] },
  { family: "EB Garamond", category: "serif", weights: [400, 500, 600, 700, 800] },
  { family: "Cormorant", category: "serif", weights: [300, 400, 500, 600, 700] },
  { family: "Libre Baskerville", category: "serif", weights: [400, 700] },
  { family: "Libre Caslon Display", category: "serif", weights: [400] },
  { family: "Libre Caslon Text", category: "serif", weights: [400, 700] },
  { family: "Merriweather", category: "serif", weights: [300, 400, 700, 900] },
  { family: "Lora", category: "serif", weights: [400, 500, 600, 700] },
  { family: "PT Serif", category: "serif", weights: [400, 700] },
  { family: "Bitter", category: "serif", weights: [300, 400, 500, 600, 700, 800, 900] },
  { family: "Crimson Pro", category: "serif", weights: [200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Crimson Text", category: "serif", weights: [400, 600, 700] },
  { family: "Cardo", category: "serif", weights: [400, 700] },
  { family: "Spectral", category: "serif", weights: [200, 300, 400, 500, 600, 700, 800] },
  { family: "Source Serif Pro", category: "serif", weights: [200, 300, 400, 600, 700, 900] },
  { family: "Source Serif 4", category: "serif", weights: [200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Noto Serif", category: "serif", weights: [400, 700] },
  { family: "Noto Serif Display", category: "serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "DM Serif Display", category: "serif", weights: [400] },
  { family: "DM Serif Text", category: "serif", weights: [400] },
  { family: "Bodoni Moda", category: "serif", weights: [400, 500, 600, 700, 800, 900] },
  { family: "Cinzel", category: "serif", weights: [400, 500, 600, 700, 800, 900] },
  { family: "Cinzel Decorative", category: "serif", weights: [400, 700, 900] },
  { family: "Old Standard TT", category: "serif", weights: [400, 700] },
  { family: "Vollkorn", category: "serif", weights: [400, 500, 600, 700, 800, 900] },
  { family: "Cardo", category: "serif", weights: [400, 700] },
  { family: "Alegreya", category: "serif", weights: [400, 500, 700, 800, 900] },
  { family: "Fraunces", category: "serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Zilla Slab", category: "serif", weights: [300, 400, 500, 600, 700] },
  { family: "Roboto Serif", category: "serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Instrument Serif", category: "serif", weights: [400] },
  { family: "Newsreader", category: "serif", weights: [200, 300, 400, 500, 600, 700, 800] },
  { family: "Prata", category: "serif", weights: [400] },
  { family: "Marcellus", category: "serif", weights: [400] },
  { family: "Cormorant Infant", category: "serif", weights: [300, 400, 500, 600, 700] },
  { family: "Cormorant SC", category: "serif", weights: [300, 400, 500, 600, 700] },
  { family: "Italiana", category: "serif", weights: [400] },
  { family: "Yeseva One", category: "serif", weights: [400] },
  { family: "Abhaya Libre", category: "serif", weights: [400, 500, 600, 700, 800] },

  // ---- Sans-serif (modern editorial + everyday) ----
  { family: "Inter", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Manrope", category: "sans-serif", weights: [200, 300, 400, 500, 600, 700, 800] },
  { family: "Space Grotesk", category: "sans-serif", weights: [300, 400, 500, 600, 700] },
  { family: "Bricolage Grotesque", category: "sans-serif", weights: [200, 300, 400, 500, 600, 700, 800] },
  { family: "DM Sans", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Plus Jakarta Sans", category: "sans-serif", weights: [200, 300, 400, 500, 600, 700, 800] },
  { family: "Poppins", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Montserrat", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Raleway", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Open Sans", category: "sans-serif", weights: [300, 400, 500, 600, 700, 800] },
  { family: "Roboto", category: "sans-serif", weights: [100, 300, 400, 500, 700, 900] },
  { family: "Lato", category: "sans-serif", weights: [100, 300, 400, 700, 900] },
  { family: "Nunito", category: "sans-serif", weights: [200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Nunito Sans", category: "sans-serif", weights: [200, 300, 400, 600, 700, 800, 900] },
  { family: "Work Sans", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Rubik", category: "sans-serif", weights: [300, 400, 500, 600, 700, 800, 900] },
  { family: "Karla", category: "sans-serif", weights: [200, 300, 400, 500, 600, 700, 800] },
  { family: "Mulish", category: "sans-serif", weights: [200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Barlow", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Archivo", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Archivo Black", category: "sans-serif", weights: [400] },
  { family: "Archivo Narrow", category: "sans-serif", weights: [400, 500, 600, 700] },
  { family: "Oswald", category: "sans-serif", weights: [200, 300, 400, 500, 600, 700] },
  { family: "Bebas Neue", category: "sans-serif", weights: [400] },
  { family: "Anton", category: "sans-serif", weights: [400] },
  { family: "Bungee", category: "sans-serif", weights: [400] },
  { family: "Bungee Inline", category: "sans-serif", weights: [400] },
  { family: "Titillium Web", category: "sans-serif", weights: [200, 300, 400, 600, 700, 900] },
  { family: "Josefin Sans", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700] },
  { family: "Quicksand", category: "sans-serif", weights: [300, 400, 500, 600, 700] },
  { family: "Fira Sans", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Source Sans 3", category: "sans-serif", weights: [200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Source Sans Pro", category: "sans-serif", weights: [200, 300, 400, 600, 700, 900] },
  { family: "IBM Plex Sans", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700] },
  { family: "Public Sans", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Onest", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Outfit", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Figtree", category: "sans-serif", weights: [300, 400, 500, 600, 700, 800, 900] },
  { family: "Sora", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800] },
  { family: "Urbanist", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Hind", category: "sans-serif", weights: [300, 400, 500, 600, 700] },
  { family: "Kanit", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Dosis", category: "sans-serif", weights: [200, 300, 400, 500, 600, 700, 800] },
  { family: "Cabin", category: "sans-serif", weights: [400, 500, 600, 700] },
  { family: "Assistant", category: "sans-serif", weights: [200, 300, 400, 500, 600, 700, 800] },
  { family: "Heebo", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Exo 2", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Overpass", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Prompt", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Signika", category: "sans-serif", weights: [300, 400, 500, 600, 700] },
  { family: "Alegreya Sans", category: "sans-serif", weights: [100, 300, 400, 500, 700, 800, 900] },
  { family: "Comfortaa", category: "sans-serif", weights: [300, 400, 500, 600, 700] },
  { family: "Chivo", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Questrial", category: "sans-serif", weights: [400] },
  { family: "Alegreya Sans SC", category: "sans-serif", weights: [100, 300, 400, 500, 700, 800, 900] },
  { family: "Big Shoulders Display", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Barlow Condensed", category: "sans-serif", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Roboto Condensed", category: "sans-serif", weights: [300, 400, 700] },
  { family: "PT Sans", category: "sans-serif", weights: [400, 700] },
  { family: "PT Sans Narrow", category: "sans-serif", weights: [400, 700] },
  { family: "Tinos", category: "serif", weights: [400, 700] },
  { family: "Syne", category: "sans-serif", weights: [400, 500, 600, 700, 800] },
  { family: "Unbounded", category: "sans-serif", weights: [200, 300, 400, 500, 600, 700, 800, 900] },

  // ---- Display / Decorative ----
  { family: "Abril Fatface", category: "display", weights: [400] },
  { family: "Bungee Shade", category: "display", weights: [400] },
  { family: "Big Shoulders Stencil Display", category: "display", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Alfa Slab One", category: "display", weights: [400] },
  { family: "Bowlby One", category: "display", weights: [400] },
  { family: "Bowlby One SC", category: "display", weights: [400] },
  { family: "Righteous", category: "display", weights: [400] },
  { family: "Ultra", category: "display", weights: [400] },
  { family: "Passion One", category: "display", weights: [400, 700, 900] },
  { family: "Staatliches", category: "display", weights: [400] },
  { family: "Six Caps", category: "display", weights: [400] },
  { family: "Rozha One", category: "display", weights: [400] },
  { family: "Sansita Swashed", category: "display", weights: [300, 400, 500, 600, 700, 800, 900] },
  { family: "Bungee Outline", category: "display", weights: [400] },
  { family: "Fjalla One", category: "display", weights: [400] },
  { family: "Squada One", category: "display", weights: [400] },
  { family: "Racing Sans One", category: "display", weights: [400] },
  { family: "Titan One", category: "display", weights: [400] },
  { family: "Big Shoulders Inline Display", category: "display", weights: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "DM Serif Display", category: "display", weights: [400] },
  { family: "Fugaz One", category: "display", weights: [400] },
  { family: "Bagel Fat One", category: "display", weights: [400] },

  // ---- Handwriting / Script ----
  { family: "Caveat", category: "handwriting", weights: [400, 500, 600, 700] },
  { family: "Dancing Script", category: "handwriting", weights: [400, 500, 600, 700] },
  { family: "Great Vibes", category: "handwriting", weights: [400] },
  { family: "Sacramento", category: "handwriting", weights: [400] },
  { family: "Pacifico", category: "handwriting", weights: [400] },
  { family: "Satisfy", category: "handwriting", weights: [400] },
  { family: "Kaushan Script", category: "handwriting", weights: [400] },
  { family: "Cookie", category: "handwriting", weights: [400] },
  { family: "Homemade Apple", category: "handwriting", weights: [400] },
  { family: "Amatic SC", category: "handwriting", weights: [400, 700] },
  { family: "Shadows Into Light", category: "handwriting", weights: [400] },
  { family: "Yellowtail", category: "handwriting", weights: [400] },
  { family: "Marck Script", category: "handwriting", weights: [400] },
  { family: "Parisienne", category: "handwriting", weights: [400] },
  { family: "Alex Brush", category: "handwriting", weights: [400] },
  { family: "Allura", category: "handwriting", weights: [400] },

  // ---- Monospace ----
  { family: "JetBrains Mono", category: "monospace", weights: [100, 200, 300, 400, 500, 600, 700, 800] },
  { family: "Fira Code", category: "monospace", weights: [300, 400, 500, 600, 700] },
  { family: "IBM Plex Mono", category: "monospace", weights: [100, 200, 300, 400, 500, 600, 700] },
  { family: "Source Code Pro", category: "monospace", weights: [200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Space Mono", category: "monospace", weights: [400, 700] },
  { family: "Roboto Mono", category: "monospace", weights: [100, 200, 300, 400, 500, 600, 700] },
  { family: "Inconsolata", category: "monospace", weights: [200, 300, 400, 500, 600, 700, 800, 900] },
  { family: "Ubuntu Mono", category: "monospace", weights: [400, 700] },
  { family: "Cousine", category: "monospace", weights: [400, 700] },
  { family: "PT Mono", category: "monospace", weights: [400] },
  { family: "Overpass Mono", category: "monospace", weights: [300, 400, 500, 600, 700] },
];

// De-duplicate by family (a couple of families appear twice in the list above
// as a hedge against typos in future edits — Set-based dedupe is cheap and
// prevents duplicate <option> entries in the dropdown).
const seen = new Set();
export const UNIQUE_GOOGLE_FONTS = GOOGLE_FONTS.filter((f) => {
  if (seen.has(f.family)) return false;
  seen.add(f.family);
  return true;
});
